import React, { useMemo, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useDebounce } from 'react-use';
import { confirmAlert } from 'react-confirm-alert';
import { HoneUserRoles } from '@hone-automation/common';

import './UsersAdmin.scss';
import { IconFileUpload, IconUser } from 'components/Icons';
import { EmailAutocomplete } from 'presentation/components/EmailAutocomplete/EmailAutocomplete';
import { LocalErrorBoundary } from 'components/ErrorBoundary';
import Loading from 'components/Loading';
import KSButton from 'presentation/components/KSButton';

import useLocationRouting from 'hooks/useLocationRouting';
import { useQueryState } from 'hooks/useQueryState';
import { useGetUserLocationsByEmail } from 'hooks/useGetUserLocationsByEmail';
import { useUpdateAdminType } from 'hooks/users/useUpdateAdminType';
import { useGetIsSiteAdmin } from 'hooks/users/useGetIsSiteAdmin';
import { useAuth } from 'context/useAuthContext';

import { NameModal } from '../../../components/Profile/NameModal';
import AddLocationModal from './AddLocationModal';
import ResetPasswordModal from './ResetPasswordModal';
import UserLocationRow from './UserLocationRow';

// ============================================================================
// Types
// ============================================================================

enum EmailError {
  Required = 'Required',
  InvalidEmail = 'Enter valid email',
}

// ============================================================================
// Sub-components
// ============================================================================

interface UserAvatarProps {
  name?: string;
}

function UserAvatar({ name }: UserAvatarProps) {
  const initials = name
    ? name
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : 'U';

  return (
    <div className="user-avatar" title={name}>
      {initials}
    </div>
  );
}

interface UserCardProps {
  user: HoneLocationUser;
  email: string;
  locationCount: number;
  onUpdateName: () => void;
  onResetPassword: () => void;
  onAddLocation: () => void;
}

function UserCard({ user, email, locationCount, onUpdateName, onResetPassword, onAddLocation }: UserCardProps) {
  const { data: siteAdminResponse, refetch } = useGetIsSiteAdmin(user?.userId);
  const emailHasKSDomain = email && email?.endsWith('@kitchensync.us');
  const isSiteAdmin = siteAdminResponse?.isSiteAdmin;

  const queryClient = useQueryClient();

  const { mutate: updateAdminType, isPending: isUpdatingAdminType } = useUpdateAdminType({
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['isSiteAdmin', user?.userId] });
    },
  });

  // Handlers
  const handleMakeSiteAdmin = useCallback(() => {
    if (!user) return;
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-ui">
          <h3>Make {email} a Site Admin?</h3>
          <p>This will grant site admin privileges to this user.</p>
          <button className="button BKForm-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="button BKForm-btn"
            onClick={() => {
              onClose();
              updateAdminType({ userId: user.userId, adminType: 'Site' });
            }}
          >
            Make Site Admin
          </button>
        </div>
      ),
    });
  }, [user, email]);

  return (
    <div className="user-card">
      <div className="user-card-left">
        <UserAvatar name={user.name} />
        <div className="user-card-info">
          <div className="user-card-name">{user.name || 'N/A'}</div>
          <div className="user-card-sub">{email}</div>
        </div>
      </div>
      <div className="user-card-right">
        <div className="locations-badge">{locationCount} locations</div>
        <div className="user-card-actions">
          <KSButton onClick={onUpdateName} label="Update" color="primary" />
          <KSButton onClick={onResetPassword} label="Reset Password" color="primary" />
          <KSButton onClick={onAddLocation} label="Add to location" color="primary" />
          {handleMakeSiteAdmin && (
            <KSButton
              onClick={handleMakeSiteAdmin}
              label={isUpdatingAdminType ? <Loading loadingCircleClass="Loading-circle-small" /> : 'Make Site Admin'}
              color="primary"
              disabled={!emailHasKSDomain || isSiteAdmin || isUpdatingAdminType}
              tooltip={isSiteAdmin ? 'User is already a site admin' : ''}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface EmptyStateProps {
  onAddLocation: () => void;
}

function EmptyState({ onAddLocation }: EmptyStateProps) {
  return (
    <div className="users-admin-empty">
      <div className="users-admin-empty__icon">
        <IconUser />
      </div>
      <h3 className="users-admin-empty__title">No locations found</h3>
      <p className="users-admin-empty__text">
        This user is not assigned to any locations yet. Add them to a location to get started.
      </p>
      <KSButton
        onClick={onAddLocation}
        label={
          <>
            <IconFileUpload />
            <span>Add user to a location</span>
          </>
        }
        color="active"
      />
    </div>
  );
}

interface LocationsTableProps {
  locations: HoneLocationUser[];
  onRefetch: () => void;
}

function LocationsTable({ locations, onRefetch }: LocationsTableProps) {
  return (
    <div className="users-admin-table">
      <div className="users-admin-table__header">
        <h3 className="users-admin-table__title">Assigned Locations</h3>
      </div>
      <div className="users-admin-table__wrapper">
        <table className="users-admin-container__table-content">
          <thead>
            <tr>
              <th>Location</th>
              <th>Role</th>
              <th>Email Opt Out</th>
              <th>Remove</th>
            </tr>
          </thead>
          <tbody>
            {locations.map(location => (
              <UserLocationRow refetch={onRefetch} key={location.locationId} location={location} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function UsersAdmin() {
  useLocationRouting(false);

  const { hasAdminRole } = useAuth();

  // Search state
  const [userEmail, setUserEmail] = useQueryState<string>('user');
  const [error, setError] = useState<EmailError | undefined>();
  const [debouncedEmail, setDebouncedEmail] = useState('');

  // Modal state
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [isAddLocationModalOpen, setIsAddLocationModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);

  // Debounce email input
  useDebounce(
    () => {
      setError(undefined);
      setDebouncedEmail(userEmail ?? '');
    },
    1000,
    [userEmail]
  );

  // Fetch user locations
  const { data: userLocations, refetch, status, isFetching, isValidEmail } = useGetUserLocationsByEmail(debouncedEmail);

  // Set error if email is invalid after debounce
  React.useEffect(() => {
    if (debouncedEmail && !isValidEmail) {
      setError(EmailError.InvalidEmail);
    }
  }, [debouncedEmail, isValidEmail]);

  // Derived state
  const locationUser = userLocations?.[0];
  const locationCount = userLocations?.length ?? 0;
  const hasLocations = !isEmpty(userLocations);
  const showInitialState = status === 'pending' && !debouncedEmail;
  const showEmptyState = status === 'success' && !hasLocations;
  const showLocationsTable = status === 'success' && hasLocations;
  const showUserCard = debouncedEmail && !error && locationUser;

  // Sort locations alphabetically
  const sortedLocations = useMemo(() => {
    if (!userLocations) return [];
    return [...userLocations].sort((a, b) => a.locationName.localeCompare(b.locationName));
  }, [userLocations]);

  const openResetPasswordModal = useCallback(() => setIsResetPasswordModalOpen(true), []);
  const closeResetPasswordModal = useCallback(() => setIsResetPasswordModalOpen(false), []);

  const toggleNameModal = useCallback(() => setIsNameModalOpen(prev => !prev), []);
  const openAddLocationModal = useCallback(() => setIsAddLocationModalOpen(true), []);
  const closeAddLocationModal = useCallback(() => setIsAddLocationModalOpen(false), []);

  return (
    <div className="users-admin-container" id="location-users-tab">
      {/* Header */}
      <div className="users-admin-wrapper">
        <div className="users-admin-header">
          <div className="users-admin-header__title-wrapper">
            <div className="users-admin-header__title-wrapper-icon">
              <IconUser />
            </div>
            <h1 className="users-admin-header__title-wrapper-text">User Management</h1>
          </div>
        </div>

        {/* Search Section */}
        <div className="users-admin-search">
          <label className="users-admin-search__label">Search by email</label>
          <span className="users-admin-search__hint">
            Enter a user email to look up their locations or add them to a new location.
          </span>
          <EmailAutocomplete
            value={userEmail ?? ''}
            onChange={setUserEmail}
            placeholder="e.g. joe@example.com"
            maxEmails={1}
          />
          <div className="users-admin-search__meta">
            {isFetching && (
              <span className="users-admin-search__loading">
                <Loading loadingCircleClass="Loading-circle-small" />
                Loading
              </span>
            )}
          </div>
          {error && <div className="users-admin-search__error">{error}</div>}
        </div>
      </div>

      {/* User Card */}
      {showUserCard && (
        <UserCard
          user={locationUser}
          email={debouncedEmail}
          locationCount={locationCount}
          onUpdateName={toggleNameModal}
          onResetPassword={openResetPasswordModal}
          onAddLocation={openAddLocationModal}
        />
      )}

      {/* Content States */}

      {showEmptyState && <EmptyState onAddLocation={openAddLocationModal} />}
      {showLocationsTable && <LocationsTable locations={sortedLocations} onRefetch={refetch} />}

      {/* Modals */}
      <AddLocationModal
        isOpen={isAddLocationModalOpen}
        onClose={closeAddLocationModal}
        email={debouncedEmail}
        onSuccess={refetch}
      />

      <ResetPasswordModal isOpen={isResetPasswordModalOpen} onClose={closeResetPasswordModal} email={debouncedEmail} />

      {locationUser && (
        <NameModal
          userId={locationUser?.userId}
          locationId={locationUser?.locationId}
          isOpen={isNameModalOpen}
          onClose={toggleNameModal}
          onSuccess={refetch}
        />
      )}
    </div>
  );
}

// ============================================================================
// Export
// ============================================================================

export default function UsersAdminWrapped() {
  return (
    <LocalErrorBoundary>
      <UsersAdmin />
    </LocalErrorBoundary>
  );
}
